import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';

/**
 * Minimal toast primitive for the platform admin.
 *
 * - `useToast()` exposes `success` / `error` / `info` helpers.
 * - Auto-dismisses after 5s; each toast also has a manual dismiss button.
 * - `aria-live="polite"` region so screen readers announce updates.
 * - Bottom-end placement using logical `end-*` so it flips correctly in RTL.
 *
 * Intentionally lightweight — no new dependencies. Mount <ToastProvider>
 * once near the app root, then call useToast() anywhere below it.
 */

export type ToastTone = 'success' | 'error' | 'info';

interface ToastRecord {
    id: string;
    tone: ToastTone;
    message: string;
}

interface ToastContextValue {
    success: (message: string) => void;
    error: (message: string) => void;
    info: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const AUTO_DISMISS_MS = 5000;

const toneStyles: Record<ToastTone, { icon: typeof CheckCircle2; ring: string; iconColor: string }> = {
    success: { icon: CheckCircle2, ring: 'border-tg-teal/40', iconColor: 'text-tg-teal' },
    error: { icon: XCircle, ring: 'border-tg-danger/40', iconColor: 'text-tg-danger' },
    info: { icon: Info, ring: 'border-tg-info/40', iconColor: 'text-tg-info' },
};

export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
    const { t } = useTranslation();
    const [toasts, setToasts] = useState<ToastRecord[]>([]);

    const dismiss = useCallback((id: string): void => {
        setToasts((current) => current.filter((toast) => toast.id !== id));
    }, []);

    const push = useCallback(
        (tone: ToastTone, message: string): void => {
            const id = crypto.randomUUID();
            setToasts((current) => [...current, { id, tone, message }]);
            window.setTimeout(() => dismiss(id), AUTO_DISMISS_MS);
        },
        [dismiss],
    );

    const value = useMemo<ToastContextValue>(
        () => ({
            success: (message: string) => push('success', message),
            error: (message: string) => push('error', message),
            info: (message: string) => push('info', message),
        }),
        [push],
    );

    return (
        <ToastContext.Provider value={value}>
            {children}
            <div
                aria-live="polite"
                aria-atomic="false"
                // Phone (<md): sit above the floating bottom tab bar instead of under it.
                className="pointer-events-none fixed bottom-[calc(var(--m-tab-bar-h)+0.75rem)] end-4 z-[100] flex w-[calc(100vw-2rem)] max-w-sm flex-col gap-2 md:bottom-[max(1rem,var(--pa-safe-bottom))]"
            >
                {toasts.map((toast) => {
                    const { icon: Icon, ring, iconColor } = toneStyles[toast.tone];
                    return (
                        <div
                            key={toast.id}
                            role="status"
                            className={cn(
                                'pointer-events-auto flex items-start gap-3 rounded-2xl border bg-white p-3.5 shadow-[var(--tg-shadow-pop)] data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:slide-in-from-bottom-2',
                                ring,
                            )}
                            data-state="open"
                        >
                            <Icon className={cn('mt-0.5 h-5 w-5 shrink-0', iconColor)} />
                            <p className="min-w-0 flex-1 break-words text-sm font-medium text-tg-ink">
                                {toast.message}
                            </p>
                            <button
                                type="button"
                                onClick={() => dismiss(toast.id)}
                                aria-label={t('common.dismiss', { defaultValue: 'Dismiss' })}
                                className="-m-2.5 grid h-10 w-10 shrink-0 place-items-center rounded-md text-tg-subtle transition-opacity hover:text-tg-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tg-teal/50"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                    );
                })}
            </div>
        </ToastContext.Provider>
    );
}

export function useToast(): ToastContextValue {
    const ctx = useContext(ToastContext);
    if (!ctx) {
        throw new Error('useToast must be used within a <ToastProvider>');
    }
    return ctx;
}
